import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { ColumnConfig } from "@shared/components/Grid/types";
import { showDialog } from "@widgets/CommonDialog";
import { showPopupModal } from "@widgets/CommonModal";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { global } from "@shared/libs/Global";
import SinglePopup from "@widgets/commonpopup/SinglePopup";

interface Props {
  Menuprops: menuInfo;
}

const SN_PMS_PJT_REG_003 = forwardRef(({ Menuprops }: Props, ref) => {
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
  });
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  const { userInfo } = global(); // Login user 정보

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setFormData({
          ...rtn.formData,
          // dt_from: "",
          // dt_to: "",
        });
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const onSearchPanelValueChanged = useCallback(
    async (event: ValueChangedEvent) => {
      if (event.colId !== "cd_pjt") return {};

      const oldPjt = String(event.oldValue ?? "").trim();
      const newPjt = String(event.newValue ?? "").trim();
      if (oldPjt === newPjt) return {};

      try {
        const param = {
          id: "popup_pjt_02",
          params: {
            cd_pjt: newPjt,
          },
        };
        const data: any = await fnGetDataSet(param);
        const project = data?.[0]?.[0];

        const nextFormData = {
          ...(searchRef.current?.getFormdata?.() ?? {}),
          cd_pjt: newPjt,
          nm_cust: project?.nm_cust ?? "",
          dt_contract: project?.dt_contract ?? "",
          dt_from: project?.dt_start ?? "",
          dt_to: project?.dt_end ?? "",
        };

        onSearch(nextFormData);

        return {
          nm_cust: project?.nm_cust ?? "",
          dt_contract: project?.dt_contract ?? "",
          dt_from: project?.dt_start ?? "",
          dt_to: project?.dt_end ?? "",
        };
      } catch (error) {
        return {
          nm_cust: "",
          dt_contract: "",
          dt_from: "",
          dt_to: "",
        };
      }
    },
    []
  );

  const onSearch = async (overrideForm?: any) => {
    const searchInfo = {
      ...(searchRef.current?.getFormdata?.() ?? {}),
      ...(overrideForm || {}),
    };
    if (!searchInfo.cd_pjt) {
      showDialog("alter", "프로젝트를 선택해 주세요.");
      return;
    }

    const modifiedRowsH = getGridCurrent("grid1").getChange();

    if (modifiedRowsH.modifiedRowcount > 0) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );

      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }

    try {
      const param = {
        id: "sn_pms_pjt_reg_003_01",
        params: searchInfo,
      };
      getGridCurrent("grid1").resetUpdate();
      const data: any = await fnGetDataSet(param);
      setDataSources({
        grid1: data[0],
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const onInsert = () => {
    const openProjectPopup = async () => {
      try {
        const searchInfo = searchRef.current?.getFormdata?.() ?? {};
        const result = await showPopupModal({
          title: "PJT 조회",
          children: (
            <SinglePopup
              code="POPUP_PJT"
              selectedData={searchInfo}
              autoSearch={false}
            />
          ),
        });

        if (!Array.isArray(result) || result.length === 0) return;

        const project = result[0] ?? {};
        const nextFormData = {
          ...searchInfo,
          cd_pjt: project?.cd_pjt ?? project?.code ?? "",
          nm_cust: project?.nm_cust ?? "",
          dt_contract: project?.dt_contract ?? "",
          dt_from: project?.dt_start ?? "",
          dt_to: project?.dt_end ?? "",
        };

        setFormData((prev: any) => ({
          ...(prev || {}),
          ...nextFormData,
        }));

        await onSearch(nextFormData);
      } catch (error: any) {
        if (error?.message) {
          showDialog("error", error.message);
        }
      }
    };

    openProjectPopup();
  };

  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      return;
    }

    return await onSave();
  };

  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const pjt_bomChanges = getGridCurrent("grid1").getChange();

      if (pjt_bomChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      // 요청 데이터 구성
      const SaveData = {
        pjt_bom: pjt_bomChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_PMS_PJT_REG_003_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const visibleIdx = focusedRowNodeH.rowIndex || 0;
      getGridCurrent("grid1").clearFocus();
      getGridCurrent("grid1").selectAndFocusRow(visibleIdx);

      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      return false;
    }
  };

  const onBeforeAddRowH = (): any => {
    try {
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const maxNoLine = getGridCurrent("grid1").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        dt_dlv: today,
        dt_plan_isu: today,
        cd_pjt: searchInfo?.cd_pjt,
        no_line: maxNoLine + 1,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
  };

  const onAddRow = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };

  const onDeleteRow = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      return;
    }

    return await onSave();
  };

  const onCellValueChanged = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowNode = event.rowNode;
    const rowData = event.data ?? rowNode?.data ?? {};

    switch (colId) {
      case "qt":
      case "um":
        if (rowNode) {
          const qt = Number(rowData.qt ?? 0);
          const um = Number(rowData.um ?? 0);
          rowNode.setDataValue("am", qt * um, "noEvent");
        }
        break;
    }
  };

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onCellValueChanged: onCellValueChanged,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div>
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        ></SearchPanel>
      </div>
      <div className="flex-1">
        <SnGrid
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{ rowSelection: "multiple", title: "프로젝트" }}
          events={grids.grid1.events}
        />
      </div>
    </div>
  );
});

export default SN_PMS_PJT_REG_003;
